import { useSWRConfig } from 'swr'

import { useSWRStore } from '@/store/swr'

import { API_BASE } from '@/api/endpoints'

export type Fetcher = <T = unknown>(_endpoint: string, _init?: RequestInit) => Promise<T>

export class FetchError extends Error {
  status: number
  info: unknown
  constructor(message: string, status: number, info?: unknown) {
    super(message)
    this.name = 'FetchError'
    this.status = status
    this.info = info
  }
}

type FetcherOptions = RequestInit & {
  baseUrl?: string
}

type UnauthorizedHandler = () => Promise<boolean>
type SessionExpiredHandler = () => void

// Fetcher autenticato (legge token dallo store)
export const createFetcher = (
  getToken: () => string | null,
  options: FetcherOptions = {},
  onUnauthorized?: UnauthorizedHandler,
  onSessionExpired?: SessionExpiredHandler
) => {
  const { baseUrl = API_BASE, headers: defaultHeaders = {} } = options
  const isDev = process.env.NODE_ENV === 'development'

  const debugLog = (event: string, payload: Record<string, unknown> = {}) => {
    if (!isDev) {
      return
    }

    console.log('[SWR fetcher]', event, payload)
  }

  return async <T = unknown>(endpoint: string, init?: RequestInit): Promise<T> => {
    const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`

    const getHeaders = () => {
      const token = getToken()
      const isFormData = init?.body instanceof FormData

      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...defaultHeaders,
        ...init?.headers
      } as Record<string, string>

      // Don't set Content-Type for FormData - browser will set it with boundary
      if (!isFormData) {
        headers['Content-Type'] = 'application/json'
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      return headers
    }

    const request = () =>
      fetch(url, {
        ...options,
        ...init,
        headers: getHeaders()
      })

    const getInfoMessage = (info: unknown) => {
      if (!info) return null

      if (typeof info === 'string') {
        return info
      }

      if (typeof info === 'object') {
        if ('message' in info && typeof info.message === 'string' && info.message.trim()) {
          return info.message
        }

        if ('errors' in info && typeof info.errors === 'object' && info.errors) {
          const errors = info.errors as Record<string, unknown>
          for (const value of Object.values(errors)) {
            if (Array.isArray(value)) {
              const message = value.find((item) => typeof item === 'string') as string | undefined
              if (message) return message
            }

            if (typeof value === 'string') {
              return value
            }
          }
        }
      }

      return null
    }

    const parseResponse = async (res: Response) => {
      if (!res.ok) {
        const info = await res.json().catch(() => null)
        const message = getInfoMessage(info) || 'API Error'
        throw new FetchError(message, res.status, info)
      }

      if (res.status === 204) {
        return undefined as T
      }

      return res.json() as Promise<T>
    }

    const getErrorPayload = (value: unknown) => {
      if (value instanceof FetchError) {
        const message = getInfoMessage(value.info) || value.message || 'Unknown error'
        return { status: value.status, info: { message } }
      }

      return {
        status: null,
        info: { message: value instanceof Error ? value.message : 'Unknown error' }
      }
    }

    try {
      const res = await request()
      return await parseResponse(res)
    } catch (error) {
      let finalError: unknown = error
      let sessionExpiredCalled = false
      const { status } = getErrorPayload(finalError)
      debugLog('request failed', { endpoint, status })

      if (status === 401 && onUnauthorized) {
        debugLog('401 detected, starting refresh', { endpoint })
        const refreshed = await onUnauthorized().catch(() => false)

        if (refreshed) {
          debugLog('refresh completed, retrying request', { endpoint })
          try {
            const retryRes = await request()
            const parsed = await parseResponse(retryRes)
            debugLog('retry succeeded', { endpoint })
            return parsed
          } catch (retryError) {
            finalError = retryError
            const { status: retryStatus } = getErrorPayload(retryError)
            debugLog('retry failed after refresh', { endpoint, status: retryStatus })
          }
        } else {
          debugLog('refresh failed', { endpoint })
          onSessionExpired?.()
          sessionExpiredCalled = true
        }
      }

      const { status: finalStatus, info } = getErrorPayload(finalError)

      if (finalStatus === 401 && !sessionExpiredCalled) {
        onSessionExpired?.()
      }

      useSWRStore
        .getState()
        .setError({ status: finalStatus, info, endpoint, timestamp: Date.now() })
      return Promise.reject(finalError)
    }
  }
}

// Hook per accedere al fetcher globale con tipi corretti
export const useSWRFetcher = () => {
  const { fetcher } = useSWRConfig()
  return fetcher as Fetcher
}
